import { toast } from 'sonner';
import { useEffect, useState } from 'react';
import { format } from 'date-fns';
import { Loader2, Users } from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { P, PermissionGuard, RequirePermission } from '@/features/permissions';
import { useMembers, useUpdateMember, useRemoveMember, type MemberOut } from '../api/members-api';
import { useRoles } from '../api/roles-api';
import { InviteUserDialog } from '../components/InviteUserDialog';
import { InvitationsTab } from '../components/InvitationsTab';
import { Card, CardContent } from '@/components/ui/card';
import { usePageHeaderSetter } from '@/components/layout/PageHeaderContext';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';

export function UsersPage() {
  return (
    <PermissionGuard permission={P.MANAGE_USERS}>
      <UsersPageContent />
    </PermissionGuard>
  );
}

function UsersPageContent() {
  const setPageHeader = usePageHeaderSetter();
  const { isLoading, data: members } = useMembers();
  const { data: roles } = useRoles();
  const updateMember = useUpdateMember();
  const removeMember = useRemoveMember();

  const [tab, setTab] = useState<'members' | 'invitations'>('members');
  const [selected, setSelected] = useState<MemberOut | null>(null);
  const [editRoleId, setEditRoleId] = useState('');
  const [editActive, setEditActive] = useState(true);
  const [editConsultant, setEditConsultant] = useState(false);

  useEffect(() => {
    setPageHeader({
      title: 'Users',
      description: 'Organization members',
      actions: <InviteUserDialog />,
    });
    return () => setPageHeader(null);
  }, [setPageHeader]);

  function openEdit(member: MemberOut) {
    setSelected(member);
    setEditRoleId(member.role_id);
    setEditActive(member.is_active);
    setEditConsultant(member.is_consultant);
  }

  async function handleSave() {
    if (!selected) return;
    try {
      await updateMember.mutateAsync({
        id: selected.id,
        role_id: editRoleId,
        is_active: editActive,
        is_consultant: editConsultant,
      });
      toast.success('Member updated');
      setSelected(null);
    } catch {
      toast.error('Failed to update member');
    }
  }

  async function handleRemove() {
    if (!selected) return;
    try {
      await removeMember.mutateAsync(selected.id);
      toast.success('Member removed');
      setSelected(null);
    } catch {
      toast.error('Failed to remove member');
    }
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-12">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  const membersList = members ?? [];

  return (
    <div className="flex flex-col gap-6 p-4 md:p-6">
      {/* Tab navigation */}
      <div className="flex gap-0 border-b">
        <button
          type="button"
          onClick={() => setTab('members')}
          className={`px-4 py-2 text-sm font-medium transition-colors ${
            tab === 'members'
              ? 'text-primary border-b-2 border-primary -mb-px'
              : 'text-muted-foreground hover:text-foreground'
          }`}
        >
          Members ({membersList.length})
        </button>
        <button
          type="button"
          onClick={() => setTab('invitations')}
          className={`px-4 py-2 text-sm font-medium transition-colors ${
            tab === 'invitations'
              ? 'text-primary border-b-2 border-primary -mb-px'
              : 'text-muted-foreground hover:text-foreground'
          }`}
        >
          Invitations
        </button>
      </div>

      {tab === 'members' && (
        <>
          {/* Stats */}
          <div className="grid gap-4 grid-cols-2 md:grid-cols-3">
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-blue-50 text-blue-600">
                    <Users className="h-5 w-5" />
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Total Members</p>
                    <p className="text-2xl font-bold">{membersList.length}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Table */}
          <div className="rounded-lg border overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow className="bg-muted/50">
                  <TableHead>Name</TableHead>
                  <TableHead className="hidden md:table-cell">Email</TableHead>
                  <TableHead>Role</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="hidden md:table-cell">Joined</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {membersList.map((member) => (
                  <TableRow
                    key={member.id}
                    className="cursor-pointer"
                    onClick={() => openEdit(member)}
                  >
                    <TableCell className="font-medium">
                      {member.user_name ?? member.user_id.slice(0, 8)}
                    </TableCell>
                    <TableCell className="hidden md:table-cell text-sm text-muted-foreground">
                      {member.user_email ?? '—'}
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline">
                        {member.role_name ?? member.role_id.slice(0, 8)}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <Badge variant={member.is_active ? 'default' : 'secondary'}>
                        {member.is_active ? 'Active' : 'Inactive'}
                      </Badge>
                    </TableCell>
                    <TableCell className="hidden md:table-cell text-sm text-muted-foreground">
                      {format(new Date(member.joined_at), 'MMM d, yyyy')}
                    </TableCell>
                  </TableRow>
                ))}
                {membersList.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={5} className="h-24 text-center text-muted-foreground">
                      No members found.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>

          <p className="text-xs text-muted-foreground text-center">
            Showing {membersList.length} members
          </p>

          {/* Edit Member Dialog */}
          <Dialog open={!!selected} onOpenChange={(v) => { if (!v) setSelected(null); }}>
            <DialogContent className="max-w-md">
              <DialogHeader>
                <DialogTitle>Edit Member</DialogTitle>
              </DialogHeader>
              {selected && (
                <div className="flex flex-col gap-4">
                  <div>
                    <p className="text-sm font-medium">{selected.user_name}</p>
                    <p className="text-sm text-muted-foreground">{selected.user_email}</p>
                  </div>

                  <div className="flex flex-col gap-2">
                    <Label>Role</Label>
                    <Select value={editRoleId} onValueChange={setEditRoleId}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {(roles ?? []).map((role) => (
                          <SelectItem key={role.id} value={role.id}>
                            {role.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="flex items-center gap-2">
                    <Label>Active</Label>
                    <input
                      type="checkbox"
                      checked={editActive}
                      className="h-4 w-4"
                      onChange={(e) => setEditActive(e.target.checked)}
                    />
                  </div>

                  <div className="flex items-center gap-2">
                    <Label>Consultant</Label>
                    <input
                      type="checkbox"
                      checked={editConsultant}
                      className="h-4 w-4"
                      onChange={(e) => setEditConsultant(e.target.checked)}
                    />
                  </div>

                  <DialogFooter className="flex-row justify-between sm:justify-between">
                    <Button
                      size="sm"
                      variant="destructive"
                      disabled={removeMember.isPending}
                      onClick={handleRemove}
                    >
                      {removeMember.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                      Remove
                    </Button>
                    <div className="flex gap-2">
                      <DialogClose asChild>
                        <Button variant="outline">Cancel</Button>
                      </DialogClose>
                      <Button disabled={updateMember.isPending} onClick={handleSave}>
                        {updateMember.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                        Save
                      </Button>
                    </div>
                  </DialogFooter>
                </div>
              )}
            </DialogContent>
          </Dialog>
        </>
      )}

      {tab === 'invitations' && <InvitationsTab />}
    </div>
  );
}
